// cleanSlate sidebar behaviour. Three pieces, all CSP-safe (wired via
// addEventListener — no inline handlers like the design mock's onclick="...").
export default function initSidebar() {
    initGroups();
    initCollapse();
    initTooltips();
}

// 1) Collapsible menu groups — a tab with children renders a trigger button + a
// submenu collapsed by default (max-height:0 in CSS). Clicking the trigger
// toggles `open` on its .zit-cs-sidebar-group, which the CSS animates (submenu
// height + caret rotation). The active group is rendered `open` by the template,
// so this only wires the toggling.
function initGroups() {
    const triggers = document.querySelectorAll('.zit-cs-sidebar-trigger');

    triggers.forEach(function (trigger) {
        trigger.addEventListener('click', function () {
            const group = trigger.closest('.zit-cs-sidebar-group');

            if (group) {
                group.classList.toggle('open');

                // keep aria-expanded in sync for assistive tech
                const expanded = group.classList.contains('open');
                trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            }
        });
    });
}

// 2) Rail collapse — the edge button toggles the `collapsed` class, narrowing the
// sidebar to an icon rail (CSS hides labels/carets/submenus and centres items).
// Behaviour by viewport:
//   - Wide (> 1024px): wide by default; the remembered state is rendered server-
//     side from the zitSidebarCollapsed cookie, and toggling persists it.
//   - Small (<= 1024px): always starts collapsed regardless of the remembered
//     state; the toggle still works (lets you expand for a session) but does NOT
//     persist. Re-evaluated only when crossing the threshold so a manual toggle
//     within a regime isn't clobbered.
const SMALL_SCREEN = '(max-width: 1024px)';

function initCollapse() {
    const sidebar = document.querySelector('.zit-cs-sidebar');
    const toggle = document.querySelector('.zit-cs-sidebar-collapse');

    if (!sidebar || !toggle) {
        return;
    }

    const smallScreen = window.matchMedia(SMALL_SCREEN);

    // Small screens start collapsed even if the cookie remembered "wide".
    if (smallScreen.matches) {
        sidebar.classList.add('collapsed');
    }

    toggle.addEventListener('click', function () {
        const collapsed = sidebar.classList.toggle('collapsed');

        // Persist only on wide screens. A cookie (not localStorage) because the
        // server reads it to render the initial class — the strict CSP rules out a
        // pre-paint inline script. Small-screen state is intentionally not remembered.
        if (!smallScreen.matches) {
            document.cookie = 'zitSidebarCollapsed=' + (collapsed ? '1' : '0') + ';path=/;max-age=31536000;SameSite=Lax';
        }
    });

    // Crossing the threshold: entering small collapses; entering wide restores the
    // remembered (cookie) state. Fires only on change, so it leaves manual toggles
    // within the same regime alone.
    smallScreen.addEventListener('change', function (event) {
        if (event.matches) {
            sidebar.classList.add('collapsed');
        } else {
            const remembered = document.cookie.indexOf('zitSidebarCollapsed=1') !== -1;
            sidebar.classList.toggle('collapsed', remembered);
        }
    });
}

// 3) Collapsed-rail tooltips — show each item's label as a floating tip on hover
// while the rail is collapsed. Positioned with the CSSOM (a fixed tip needs runtime
// coordinates); CSSOM property assignment is allowed under CSP, unlike a static
// style="" attribute in markup.
function initTooltips() {
    const sidebar = document.querySelector('.zit-cs-sidebar');

    if (!sidebar) {
        return;
    }

    const links = sidebar.querySelectorAll('.zit-cs-sidebar-link');

    links.forEach(function (link) {
        const tip = link.querySelector('.zit-cs-sidebar-tooltip');

        if (!tip) {
            return;
        }

        link.addEventListener('mouseenter', function () {
            // Only when collapsed — the rail always carries the `collapsed` class
            // while collapsed (set by initCollapse on small screens too), so a class
            // check is enough. Expanded items already show their label.
            if (!sidebar.classList.contains('collapsed')) {
                return;
            }

            const rect = link.getBoundingClientRect();
            tip.style.left = (rect.right + 8) + 'px';
            tip.style.top = (rect.top + rect.height / 2) + 'px';
            tip.style.display = 'block';
        });

        link.addEventListener('mouseleave', function () {
            tip.style.display = 'none';
        });
    });
}
